import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import {
  Activity, AlertTriangle, CheckCircle2, Clock, RefreshCw, TrendingUp,
  Webhook, Zap, ChevronDown, ChevronRight, Filter, Search,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WINDOW_OPTIONS = [
  { hours: 1, label: '1h' },
  { hours: 6, label: '6h' },
  { hours: 24, label: '24h' },
  { hours: 72, label: '72h' },
  { hours: 168, label: '7d' },
  { hours: 720, label: '30d' },
];

function StatCard({ label, value, sub, color = 'slate', icon: Icon }) {
  const palettes = {
    slate: 'text-slate-900',
    emerald: 'text-emerald-700',
    amber: 'text-amber-700',
    red: 'text-red-700',
    blue: 'text-blue-700',
  };
  return (
    <div className="rounded-xl border bg-white p-4">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[11px] uppercase text-slate-500 font-semibold tracking-wide">{label}</div>
        {Icon && <Icon className={`w-4 h-4 ${palettes[color]}`} />}
      </div>
      <div className={`text-2xl font-bold ${palettes[color]}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function StatusBadge({ code }) {
  const c = code || 0;
  let cls = 'bg-gray-100 text-gray-700';
  if (c >= 500) cls = 'bg-red-100 text-red-800';
  else if (c >= 400) cls = 'bg-amber-100 text-amber-800';
  else if (c >= 200 && c < 300) cls = 'bg-emerald-100 text-emerald-800';
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-mono font-semibold ${cls}`}>{c}</span>;
}

function MethodBadge({ method }) {
  const m = (method || 'GET').toUpperCase();
  const palette = {
    GET: 'bg-blue-50 text-blue-700 border-blue-200',
    POST: 'bg-green-50 text-green-700 border-green-200',
    PUT: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    DELETE: 'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold border ${palette[m] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>{m}</span>
  );
}

function TimelineSparkline({ timeline }) {
  if (!timeline || timeline.length === 0) {
    return <div className="text-sm text-slate-400 py-12 text-center">Sin datos en este rango.</div>;
  }
  const maxTotal = Math.max(...timeline.map(b => b.total), 1);
  return (
    <div className="flex items-end gap-1 h-32 px-2">
      {timeline.map((b, idx) => {
        const successPct = b.total ? (b.success / maxTotal) * 100 : 0;
        const errorPct = b.total ? (b.errors / maxTotal) * 100 : 0;
        const date = new Date(b.bucket_start);
        const tip = `${date.toLocaleString('es-VE', { hour12: false })}\nTotal: ${b.total} (✓ ${b.success} | ✗ ${b.errors})\nLatencia prom: ${b.avg_latency_ms}ms`;
        return (
          <div key={idx} className="flex-1 flex flex-col-reverse items-center gap-px group relative" title={tip}>
            <div style={{ height: `${successPct}%` }} className="w-full bg-emerald-500 rounded-sm min-h-[2px]" />
            <div style={{ height: `${errorPct}%` }} className="w-full bg-red-500 rounded-sm" />
          </div>
        );
      })}
    </div>
  );
}

function LogRow({ log, expanded, onToggle }) {
  const ts = new Date(log.timestamp).toLocaleString('es-VE', { hour12: false });
  return (
    <>
      <tr onClick={onToggle} className="cursor-pointer hover:bg-slate-50" data-testid={`api-health-log-${log.log_id}`}>
        <td className="px-3 py-2 text-[11px] text-gray-500 font-mono whitespace-nowrap">
          <div className="flex items-center gap-2">
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            {ts}
          </div>
        </td>
        <td className="px-3 py-2"><MethodBadge method={log.method} /></td>
        <td className="px-3 py-2 text-[12px] font-mono text-gray-800 max-w-[280px] truncate" title={log.path}>{log.path}</td>
        <td className="px-3 py-2"><StatusBadge code={log.status_code} /></td>
        <td className="px-3 py-2 text-[11px] text-gray-600 whitespace-nowrap">{log.duration_ms ?? '-'} ms</td>
        <td className="px-3 py-2 text-[11px] text-gray-600 font-mono whitespace-nowrap">{log.ip || '-'}</td>
      </tr>
      {expanded && (
        <tr className="bg-slate-50">
          <td colSpan={6} className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-[10px] font-bold uppercase text-slate-500 mb-2">Request</h4>
                <pre className="text-[11px] whitespace-pre-wrap break-words bg-slate-900 text-slate-100 p-3 rounded font-mono leading-relaxed max-h-56 overflow-auto">{JSON.stringify(log.request_body, null, 2)}</pre>
              </div>
              <div>
                <h4 className="text-[10px] font-bold uppercase text-slate-500 mb-2">Response</h4>
                <pre className="text-[11px] whitespace-pre-wrap break-words bg-slate-900 text-slate-100 p-3 rounded font-mono leading-relaxed max-h-56 overflow-auto">{JSON.stringify(log.response_body, null, 2)}</pre>
              </div>
            </div>
            <div className="text-[10px] text-slate-500 mt-2 font-mono">User-Agent: {log.user_agent}</div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function ApiHealth() {
  const [hours, setHours] = useState(24);
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedLogId, setExpandedLogId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const bucketMin = hours <= 6 ? 15 : hours <= 24 ? 60 : hours <= 72 ? 180 : 720;
      const [s, t, l] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/merchant/api-health/summary?hours=${hours}`, { headers }),
        axios.get(`${BACKEND_URL}/api/merchant/api-health/timeline?hours=${hours}&bucket_minutes=${bucketMin}`, { headers }),
        axios.get(`${BACKEND_URL}/api/merchant/api-health/recent-logs?limit=100&only_errors=${onlyErrors}`, { headers }),
      ]);
      setSummary(s.data);
      setTimeline(t.data?.timeline || []);
      setLogs(l.data || []);
    } catch (e) {
      console.error('api-health load failed:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hours, onlyErrors]);

  const filteredLogs = logs.filter(log => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (log.path || '').toLowerCase().includes(q)
      || JSON.stringify(log.request_body || '').toLowerCase().includes(q)
      || JSON.stringify(log.response_body || '').toLowerCase().includes(q)
      || (log.ip || '').toLowerCase().includes(q)
    );
  });

  const incoming = summary?.incoming_api;
  const wh = summary?.outgoing_webhooks;

  const overallStatus = (() => {
    if (!summary) return null;
    const errRate = incoming?.total_requests
      ? (incoming.client_4xx + incoming.server_5xx) / incoming.total_requests
      : 0;
    if (errRate > 0.2 || (wh?.disabled_circuit_breaker || 0) > 0) return 'degraded';
    if (errRate > 0.05) return 'warning';
    return 'healthy';
  })();

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="api-health-page">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Activity className="w-6 h-6" /> API Health
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Salud de tus integraciones con Caura Pay: requests entrantes, latencia y webhooks salientes.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {overallStatus && (
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                overallStatus === 'healthy' ? 'bg-emerald-100 text-emerald-800'
                : overallStatus === 'warning' ? 'bg-amber-100 text-amber-800'
                : 'bg-red-100 text-red-800'
              }`}>
                {overallStatus === 'healthy' && '● Healthy'}
                {overallStatus === 'warning' && '● Warning'}
                {overallStatus === 'degraded' && '● Degraded'}
              </span>
            )}
            <button
              onClick={load}
              disabled={loading}
              data-testid="api-health-refresh"
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border bg-white hover:bg-slate-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refrescar
            </button>
          </div>
        </div>

        {/* Window selector */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          <span className="text-xs uppercase text-slate-500 font-semibold tracking-wide">Ventana</span>
          {WINDOW_OPTIONS.map(opt => (
            <button
              key={opt.hours}
              onClick={() => setHours(opt.hours)}
              data-testid={`api-health-window-${opt.hours}`}
              className={`px-3 py-1 rounded-lg text-xs font-medium border ${hours === opt.hours ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Stats — Incoming API */}
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4" /> Requests entrantes (tu integración → Caura Pay)
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Total requests" value={incoming?.total_requests ?? '-'} icon={Activity} />
            <StatCard label="Success rate" value={`${incoming?.success_rate_pct ?? 0}%`} color="emerald" icon={CheckCircle2} />
            <StatCard label="Errores 4xx/5xx" value={(incoming?.client_4xx || 0) + (incoming?.server_5xx || 0)} sub={`${incoming?.client_4xx || 0} client · ${incoming?.server_5xx || 0} server`} color="red" icon={AlertTriangle} />
            <StatCard label="Latencia prom." value={`${incoming?.avg_latency_ms ?? 0} ms`} color="blue" icon={Clock} />
          </div>
        </div>

        {/* Timeline */}
        <div className="mb-6 rounded-xl border bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> Volumen de requests
            </h3>
            <div className="text-[10px] text-slate-500 flex items-center gap-3">
              <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-sm bg-emerald-500"></div>Success</span>
              <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-sm bg-red-500"></div>Error</span>
            </div>
          </div>
          <TimelineSparkline timeline={timeline} />
        </div>

        {/* Top endpoints + Webhooks */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <div className="rounded-xl border bg-white p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Activity className="w-4 h-4" /> Top endpoints
            </h3>
            {(incoming?.top_endpoints || []).length === 0 ? (
              <p className="text-sm text-slate-400">Sin requests en este rango.</p>
            ) : (
              <ul className="space-y-2" data-testid="api-health-top-endpoints">
                {incoming.top_endpoints.map((ep, i) => (
                  <li key={i} className="flex items-center justify-between text-sm">
                    <span className="font-mono text-xs truncate flex-1 mr-2">{ep.endpoint}</span>
                    <span className="text-slate-500 text-xs font-semibold">{ep.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-xl border bg-white p-4" data-testid="api-health-webhooks">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Webhook className="w-4 h-4" /> Webhooks salientes (Caura Pay → tu integración)
            </h3>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <StatCard label="Activos" value={wh?.active_webhooks ?? 0} color="emerald" />
              <StatCard label="Deshabilitados (CB)" value={wh?.disabled_circuit_breaker ?? 0} color={wh?.disabled_circuit_breaker > 0 ? 'red' : 'slate'} />
              <StatCard label="Deliveries" value={wh?.total_deliveries ?? 0} />
              <StatCard label="Success rate" value={`${wh?.success_rate_pct ?? 0}%`} color="emerald" />
            </div>
            {(wh?.disabled_circuit_breaker || 0) > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5" />
                <div>
                  <strong>{wh.disabled_circuit_breaker}</strong> webhook(s) deshabilitado(s) por circuit breaker (20+ fallas seguidas).
                  Ve a Webhooks → Reset circuit breaker para reactivarlos.
                </div>
              </div>
            )}
            <div className="text-[10px] text-slate-500 mt-2">
              Backoff de retries: 1m → 5m → 30m → 2h → 12h → 24h (máx 7 intentos)
            </div>
          </div>
        </div>

        {/* Recent errors */}
        {(incoming?.recent_errors || []).length > 0 && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-6">
            <h3 className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Errores recientes
            </h3>
            <ul className="space-y-1 text-xs">
              {incoming.recent_errors.map((e, i) => (
                <li key={i} className="flex items-center gap-2 font-mono">
                  <span className="text-amber-700">{new Date(e.timestamp).toLocaleString('es-VE', { hour12: false })}</span>
                  <MethodBadge method={e.method} />
                  <span className="truncate flex-1">{e.path}</span>
                  <StatusBadge code={e.status_code} />
                  <span className="text-amber-700">{e.duration_ms}ms</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Logs detallados */}
        <div className="rounded-xl border bg-white">
          <div className="p-4 border-b flex items-center justify-between flex-wrap gap-3">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Filter className="w-4 h-4" /> Últimos requests
            </h3>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={onlyErrors}
                  onChange={e => setOnlyErrors(e.target.checked)}
                  data-testid="api-health-only-errors"
                  className="w-4 h-4"
                />
                Solo errores
              </label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Buscar..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  data-testid="api-health-search"
                  className="pl-7 pr-3 py-1 border rounded-lg text-sm w-48"
                />
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2 text-left">Timestamp</th>
                  <th className="px-3 py-2 text-left">Método</th>
                  <th className="px-3 py-2 text-left">Path</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Lat.</th>
                  <th className="px-3 py-2 text-left">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredLogs.length === 0 && !loading && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500">Sin requests aún. Cuando tu integración llame a /api/v1/* aparecerán aquí.</td></tr>
                )}
                {filteredLogs.map(log => (
                  <LogRow
                    key={log.log_id}
                    log={log}
                    expanded={expandedLogId === log.log_id}
                    onToggle={() => setExpandedLogId(expandedLogId === log.log_id ? null : log.log_id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="text-[10px] text-slate-500 mt-4">
          Rate limits: 60 req/min por IP · 120 req/min por API key. Los datos se actualizan cada vez que refrescas.
        </div>
      </div>
    </Layout>
  );
}
